<script setup>
  import { ref, watchEffect } from 'vue';
  import { useSheetController } from '@/composables/useSheetsController';
  import { useMediaQuery } from '@/composables/useMediaQuery'

  const model = defineModel();

  const isDesktop = useMediaQuery('(min-width: 840px)')
  
  const props = defineProps({
    sheet: {
      required: true
    },
    type: {
      type: String,
      required: true
    },
    resetSheetHeight: {
      type: Boolean,
      default: false
    }
  });

  let dragging = ref(false);
  let startDragY = ref(0);
  let dragDirection = ref(null);
  let dragSpeedReadings = ref([]);
  let previousYPos = ref(null);
  let previousMoveTimestamp = ref(null);
  let sheetCurrentHeight = ref(null);
  let sheetDraggingHeight = ref(null);


  let sheetMinimizedHeight = ref(null);
  let sheetSmallHeight = ref(null);
  let sheetMidHeight = ref(null);
  let sheetMaximizedHeight = ref(null);
  let sheetMinimizedHeightPixels = ref(null);
  let sheetSmallHeightPixels = ref(null);
  let sheetMidHeightPixels = ref(null);
  let sheetMaximizedHeightPixels = ref(null);
  let sheetResizer = ref(null);
  let sheet = ref(null);

  const handlePointerDown = (e) => {
    dragging.value = true;
    startDragY.value = e.y;
    e.target.setPointerCapture(e.pointerId);
    sheetCurrentHeight.value = sheet.value.getBoundingClientRect().height;
    sheet.value.dataset.dragging = true;

    sheetResizer.value.addEventListener('pointermove', throttlePointerMove);
    sheetResizer.value.addEventListener('pointerup', handlePointerUp);
    sheetResizer.value.addEventListener('pointercancel', handlePointerCancel);
  }

  const handlePointerMove = (e) => {
    // if (!dragging.value) return;

    const now = Date.now();
    const pixelsTraveled = (previousYPos.value || e.y) - e.y;
    const timeDelta = Math.abs((previousMoveTimestamp.value || now) - now);
    const pixelsPerMS = !pixelsTraveled || !timeDelta ? 0 : Math.abs(pixelsTraveled) / timeDelta;

    dragDirection.value = pixelsTraveled < 0 ? 'down' : 'up';
    dragSpeedReadings.value.push(pixelsPerMS);

    sheetDraggingHeight.value = sheetCurrentHeight.value + (startDragY.value - e.y);
    previousYPos.value = e.y;
    previousMoveTimestamp.value = now;

    const cssHeight = 
      sheetDraggingHeight.value > sheetMaximizedHeightPixels.value
      ? sheetMaximizedHeight.value
      : sheetDraggingHeight.value < sheetMinimizedHeightPixels.value
      ? sheetMinimizedHeight.value
      : `${sheetDraggingHeight.value}px`;

    document.documentElement.style.setProperty('--sheet-resized-height', cssHeight);
  }

  const throttlePointerMove = (e) => {
    if (dragging.value) {
      requestAnimationFrame(() => handlePointerMove(e))
    }
  };

  const handlePointerUp = (e) => {
    handlePointerCancel();

    const averageDragSpeed = dragSpeedReadings.value.reduce((prev, curr) => prev + curr, 0) / dragSpeedReadings.value.length;
    let sheetFinalHeight;

    if (averageDragSpeed > .8) {
      model.value = dragDirection.value === 'up' ? 'maximized' : 'minimized';
      sheetFinalHeight = dragDirection.value === 'up' ? sheetMaximizedHeight.value :  sheetMinimizedHeight.value;

      if (!isDesktop.value && dragDirection.value === 'down') useSheetController.closeAllSheets();

      document.documentElement.style.setProperty('--sheet-resized-height', sheetFinalHeight);

      dragSpeedReadings.value = [];
      dragDirection.value = null;
      
      return;
    }

    if (sheetDraggingHeight.value < sheetMinimizedHeightPixels.value / 2) {
      useSheetController.toggleSheet(props.type);
      return;
    }
    else if (sheetDraggingHeight.value < sheetMaximizedHeightPixels.value * .25) {
      model.value = 'minimized';
      sheetFinalHeight = sheetMinimizedHeight.value;
    }
    else if (sheetDraggingHeight.value < sheetMaximizedHeightPixels.value * .35) {
      model.value = 'small';
      sheetFinalHeight = sheetSmallHeight.value;
    }
    else if (sheetDraggingHeight.value < sheetMaximizedHeightPixels.value * .75) {
      model.value = 'mid';
      sheetFinalHeight = sheetMidHeight.value;
    } else {
      model.value = 'maximized';
      sheetFinalHeight = sheetMaximizedHeight.value;
    }

    document.documentElement.style.setProperty('--sheet-resized-height', sheetFinalHeight);
  }
  
  function handlePointerCancel(e) {
    dragging.value = false;
    previousYPos.value = 0;
    
    sheet.value.removeAttribute('data-dragging');

    sheetResizer.value.removeEventListener('pointermove', throttlePointerMove);
    sheetResizer.value.removeEventListener('pointerup', handlePointerUp);
    sheetResizer.value.removeEventListener('pointercancel', handlePointerCancel);
  }

  function getPixelHeight(property) {
    const cssValues = getComputedStyle(sheet.value);
    const cssHeight = cssValues.getPropertyValue(property);
    let heightBasis;

    let heightValue = cssHeight.replace(/(?:px|dvh|vh|%)/, (match) => {
      heightBasis = 
        (match === 'dvh' || match === 'vh') 
        ? 'window' 
        : match === '%'
        ? 'parent'
        : 'pixel';
      
      return '';
    });
    
    if (heightBasis === 'window') {
      heightValue = (window.innerHeight / 100) * parseInt(heightValue);
    }

    if (heightBasis === 'parent') {
      heightValue = (sheet.value.parentElement.getBoundingClientRect().height / 100) * parseInt(heightValue);
    }

    return [parseInt(heightValue), cssHeight];
  }

  function setInitialValues() {
    const minimizedHeight = getPixelHeight('--minimized-height');
    const smallHeight = getPixelHeight('--small-height');
    const midHeight = getPixelHeight('--mid-height');
    const maximizedHeight = getPixelHeight('--maximized-height');

    sheetMinimizedHeight.value = minimizedHeight[1];
    sheetSmallHeight.value = smallHeight[1];
    sheetMidHeight.value = midHeight[1];
    sheetMaximizedHeight.value = maximizedHeight[1];
    
    sheetMinimizedHeightPixels.value = minimizedHeight[0];
    sheetSmallHeightPixels.value = smallHeight[0];
    sheetMidHeightPixels.value = midHeight[0];
    sheetMaximizedHeightPixels.value = maximizedHeight[0];
  }

  watchEffect(() => {
    if (!props.sheet) return;

    sheet.value = props.sheet;

    setInitialValues();
  });

  watchEffect(() => {
    props.resetSheetHeight && document.documentElement.style.setProperty('--sheet-resized-height', '')
    })
</script>

<template>
  <div
    @pointerdown.prevent="handlePointerDown"
    class="jwx-sheet__drag-resize-bar"
    ref="sheetResizer"
  ></div>
</template>